import React from 'react';

export type ConfirmationVariant = 'danger' | 'warning' | 'info' | 'success';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmationVariant;
  onConfirm: () => void;
  onCancel: () => void;
  confirmDisabled?: boolean;
  hideCancelButton?: boolean;
  children?: React.ReactNode;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Подтвердить',
  cancelText = 'Отмена',
  variant = 'warning',
  onConfirm,
  onCancel,
  confirmDisabled = false,
  hideCancelButton = false,
  children
}) => {
  if (!isOpen) return null;

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          button: 'bg-red-900 hover:bg-red-800 border-red-600',
          border: 'border-red-500'
        };
      case 'warning':
        return {
          button: 'bg-orange-900 hover:bg-orange-800 border-orange-600',
          border: 'border-orange-500'
        };
      case 'info':
        return {
          button: 'bg-purple-900 hover:bg-purple-800 border-purple-600',
          border: 'border-purple-500'
        };
      case 'success':
        return {
          button: 'bg-green-900 hover:bg-green-800 border-green-600',
          border: 'border-green-500'
        };
      default:
        return {
          button: 'bg-orange-900 hover:bg-orange-800 border-orange-600',
          border: 'border-orange-500'
        };
    }
  };

  const styles = getVariantStyles();

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter' && !confirmDisabled) {
      onConfirm();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fadeIn"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={`bg-gray-900 border-2 ${styles.border} rounded-lg p-6 max-w-md w-full mx-4 animate-scaleIn`}>
        <h3 className="text-xl font-bold text-white mb-4 font-gaming">{title}</h3>
        <p className="text-gray-300 mb-4">{message}</p>
        {children && <div className="mb-4">{children}</div>}
        <div className="flex gap-3 justify-end">
          {!hideCancelButton && (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded text-white transition-colors font-gaming"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={`px-4 py-2 ${styles.button} border rounded text-white transition-colors font-gaming ${
              confirmDisabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            autoFocus={!confirmDisabled}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;